import { encodeFunctionData, parseAbi, getAddress, formatEther } from 'viem';
import { supabase } from './supabase';
import { publicClient, getEvmProvider, ensureEvmChain, waitForTransactionReceipt } from './evmNetwork';
import { LOSS_REWARD_POOL, LOSS_REWARD_POOL_V2 } from './uniswapAddresses';
import { computeMinAssetOut, isV2Configured } from './rewardAssets';
import { getStoredSession, fetchLossRewardData } from './lossRewardAuth';
import { claimLeaf, verifyClaimProof, claimDeadline, explainClaimRevert, type ClaimEpochContext } from './claimRevert';

// Includes LossRewardPool's custom errors so a pre-flight simulation of a claim decodes a
// revert to its name (e.g. AlreadyClaimed / InvalidProof) instead of a bare 4-byte selector.
const LOSS_POOL_ABI = parseAbi([
  'function getUnallocatedBalance(address token) view returns (uint256)',
  'function totalDeposited(address token) view returns (uint256)',
  'function totalAllocated(address token) view returns (uint256)',
  'function totalClaimed(address token) view returns (uint256)',
  'function hasClaimed(address token, uint256 epochId, address account) view returns (bool)',
  'function epochMerkleRoots(address token, uint256 epochId) view returns (bytes32)',
  'function claimReward(address token, uint256 epochId, uint256 amount, bytes32[] calldata merkleProof)',
  'function claimBatch(address token, uint256[] calldata epochIds, uint256[] calldata amounts, bytes32[][] calldata merkleProofs)',
  // LossRewardPoolV2 (stock-configured tokens revert UseClaimAs on the V1 signatures)
  'function claimRewardAs(address token, uint256 epochId, uint256 amount, bytes32[] calldata merkleProof, uint256 minAssetOut, uint256 deadline)',
  'function claimBatchAs(address token, uint256[] calldata epochIds, uint256[] calldata amounts, bytes32[][] calldata merkleProofs, uint256 minAssetOut, uint256 deadline)',
  'error UseClaimAs()',
  'error DeadlineExpired()',
  'error MinOutNotMet(uint256 amountOut, uint256 minAssetOut)',
  'error EpochNotPublished()',
  'error AlreadyClaimed()',
  'error InvalidProof()',
  'error EthTransferFailed()',
  'error ArrayLengthMismatch()',
  'error ReentrancyGuardReentrantCall()',
]);

/** Snapshot interval: 5 minutes (300 seconds) */
export const SNAPSHOT_INTERVAL_SECONDS = 300;
export const SNAPSHOT_INTERVAL_MINUTES = 5;

/**
 * Accurately formats ETH prices including sub-microETH values (e.g. 2e-9 on bonding curve).
 */
export const formatLossRewardEthPrice = (priceEth: number): string => {
  if (!Number.isFinite(priceEth) || priceEth <= 0) return '0.000000 ETH';
  if (priceEth < 0.00001) {
    const formatted = priceEth.toFixed(18).replace(/(\.\d*?[1-9])0+$|\.0+$/, '$1');
    return `${formatted} ETH`;
  }
  if (priceEth < 1) {
    return `${priceEth.toFixed(6)} ETH`;
  }
  return `${priceEth.toFixed(4)} ETH`;
};

export type HolderCostBasis = {
  tokenAddress: string;
  walletAddress: string;
  tokenBalance: number;
  totalInvestedEth: number;
  avgCostBasisEth: number;
  isEligible: boolean;
  isUnderwaterSeller: boolean;
};

export type UnrealizedLossStats = {
  tokenBalance: number;
  costBasisEth: number;
  currentPriceEth: number;
  unrealizedLossEth: number;
  unrealizedLossPct: number;
  theoreticalRewardEth: number;
  isUnderwater: boolean;
  isEligible: boolean;
};

export type UnclaimedEpoch = {
  id: number;
  epochId: number;
  epochNumber: number;
  finalRewardEth: number;
  /** Exact wei amount as the gateway computed it (BigInt(Math.round(finalRewardEth * 1e18)).toString()). */
  amountWei?: string;
  merkleProof: string[];
  /** Pool the epoch was published on (gateway `poolAddress`); absent/null = the V1 pool. */
  poolAddress?: string | null;
};

export type ClaimableRewardsState = {
  unclaimedEpochs: UnclaimedEpoch[];
  totalClaimableEth: number;
  pendingEpochs: UnclaimedEpoch[];
  totalPendingEth: number;
};

/**
 * Fetch a holder's cost basis and eligibility status via the secure authenticated gateway.
 */
export const getHolderCostBasis = async (
  tokenAddress: string,
  walletAddress: string
): Promise<HolderCostBasis | null> => {
  if (!tokenAddress || !walletAddress) return null;
  const token = tokenAddress.toLowerCase();
  const wallet = walletAddress.toLowerCase();

  try {
    // If a session exists, use authenticated gateway query
    if (getStoredSession(wallet)) {
      const data = await fetchLossRewardData(token, wallet);
      return data.costBasis;
    }
  } catch (err) {
    console.warn('Authenticated cost basis query skipped/failed:', err);
  }

  // Default unauthenticated baseline state
  return {
    tokenAddress: token,
    walletAddress: wallet,
    tokenBalance: 0,
    totalInvestedEth: 0,
    avgCostBasisEth: 0,
    isEligible: true,
    isUnderwaterSeller: false,
  };
};

/**
 * Calculate real-time unrealized loss and 10% theoretical reward stats.
 */
export const calculateUnrealizedLossStats = (
  costBasis: HolderCostBasis | null,
  currentPriceEth: number
): UnrealizedLossStats => {
  if (!costBasis || costBasis.tokenBalance <= 0 || costBasis.avgCostBasisEth <= 0) {
    return {
      tokenBalance: costBasis?.tokenBalance || 0,
      costBasisEth: 0,
      currentPriceEth,
      unrealizedLossEth: 0,
      unrealizedLossPct: 0,
      theoreticalRewardEth: 0,
      isUnderwater: false,
      isEligible: costBasis?.isEligible ?? true,
    };
  }

  const { tokenBalance, avgCostBasisEth, totalInvestedEth, isEligible, isUnderwaterSeller } = costBasis;
  const currentValEth = tokenBalance * currentPriceEth;
  const isUnderwater = currentPriceEth < avgCostBasisEth;

  const unrealizedLossEth = isUnderwater ? Math.max(0, totalInvestedEth - currentValEth) : 0;
  const unrealizedLossPct = totalInvestedEth > 0 ? (unrealizedLossEth / totalInvestedEth) * 100 : 0;
  const theoreticalRewardEth = (isEligible && !isUnderwaterSeller) ? 0.10 * unrealizedLossEth : 0;

  return {
    tokenBalance,
    costBasisEth: avgCostBasisEth,
    currentPriceEth,
    unrealizedLossEth,
    unrealizedLossPct,
    theoreticalRewardEth,
    isUnderwater,
    isEligible: isEligible && !isUnderwaterSeller,
  };
};

/**
 * Fetch all uncollected Merkle reward proofs for the connected wallet via the secure authenticated gateway.
 */
export const getClaimableRewards = async (
  tokenAddress: string,
  walletAddress: string
): Promise<ClaimableRewardsState> => {
  if (!tokenAddress || !walletAddress) {
    return { unclaimedEpochs: [], totalClaimableEth: 0, pendingEpochs: [], totalPendingEth: 0 };
  }

  const token = tokenAddress.toLowerCase();
  const wallet = walletAddress.toLowerCase();

  try {
    if (getStoredSession(wallet)) {
      const data = await fetchLossRewardData(token, wallet);
      return data.claimable;
    }
  } catch (err) {
    console.warn('Authenticated claimable rewards query skipped/failed:', err);
  }

  return { unclaimedEpochs: [], totalClaimableEth: 0, pendingEpochs: [], totalPendingEth: 0 };
};

/**
 * Claim one or more epochs — a thin alias for claimBatchRewards kept for existing call sites.
 */
export const claimReward = async (
  tokenAddress: string,
  walletAddress: string,
  _epochNumber?: number,
  _amountEth?: number,
  _merkleProof?: string[],
  _epochId?: number
) => {
  return claimBatchRewards(tokenAddress, walletAddress);
};

export type ClaimResult = {
  success: boolean;
  txHash: string | null;
  /** One transaction per pool claimed from (V1, then V2). */
  txHashes?: string[];
  claimedEth?: string;
  alreadyClaimed?: boolean;
  message?: string;
};

/**
 * Claim every uncollected published epoch for (token, wallet) in ONE transaction, signed and sent
 * by the user's own connected wallet — the same eth_sendTransaction path buys and sells use.
 *
 * Why not the gateway's relayer: LossRewardPool binds each Merkle leaf (and the ETH payout) to
 * msg.sender (`_claimEpoch(..., msg.sender)`), and the worker builds leaves for the HOLDER. A claim
 * relayed from the operator wallet therefore reverts InvalidProof for every wallet except the
 * operator itself — and even a matching leaf would pay the operator, not the holder. Only the
 * holder's wallet can be msg.sender, so the holder must sign.
 *
 * The authenticated gateway is still the source of truth for WHAT is claimable (`/query` returns
 * the unclaimed epochs, exact wei amounts and Merkle proofs, reconciled against on-chain
 * hasClaimed); callers may pass that list in to avoid a second round-trip.
 */
export const claimBatchRewards = async (
  tokenAddress: string,
  walletAddress: string,
  unclaimedEpochs?: UnclaimedEpoch[],
  options: { slippagePct?: number } = {}
): Promise<ClaimResult> => {
  if (!tokenAddress || !walletAddress) {
    throw new Error('Token address and connected wallet are required.');
  }
  const token = getAddress(tokenAddress);
  const wallet = getAddress(walletAddress);

  // 1. What is claimable — from the caller's already-fetched state, else the authenticated gateway.
  let epochs = (unclaimedEpochs || []).filter((e) => Number.isFinite(e.epochNumber));
  if (epochs.length === 0) {
    const data = await fetchLossRewardData(token, wallet);
    epochs = data.claimable.unclaimedEpochs || [];
  }
  if (epochs.length === 0) {
    return { success: true, txHash: null, claimedEth: '0', alreadyClaimed: true, message: 'No claimable rewards available.' };
  }

  // 2. The connected wallet MUST be the holder the proofs were built for.
  const provider = getEvmProvider();
  if (!provider) throw new Error('No EVM wallet detected. Please connect your wallet.');
  await ensureEvmChain();
  const accounts = (await provider.request({ method: 'eth_accounts' })) as string[];
  const sender = accounts?.[0];
  if (!sender) throw new Error('Wallet is connected, but no active account was found.');
  if (getAddress(sender) !== wallet) {
    throw new Error(
      `The wallet's active account (${sender.slice(0, 6)}…${sender.slice(-4)}) is not the holder these rewards belong to ` +
        `(${wallet.slice(0, 6)}…${wallet.slice(-4)}). Switch accounts in your wallet and try again.`
    );
  }

  const v1 = getAddress(LOSS_REWARD_POOL);
  const v2 = isV2Configured() ? getAddress(LOSS_REWARD_POOL_V2) : null;
  const txHashes: string[] = [];
  let totalWei = 0n;
  let anyAlreadyClaimed = false;

  // Chain clock, once: the claim deadline is measured against block.timestamp, so it is derived from
  // the chain (never only the device clock), and the skew is reported if a deadline ever expires.
  let chainTs: number | null = null;
  try {
    chainTs = Number((await publicClient.getBlock({ blockTag: 'latest' })).timestamp);
  } catch {
    chainTs = null;
  }
  const chainSkewSec = chainTs === null ? null : chainTs - Math.floor(Date.now() / 1000);

  // 3. Group by the pool each epoch was published on. Old epochs (no poolAddress) live on V1 and
  //    keep the V1 signatures; V2 epochs use claim*As with the user's minAssetOut + a deadline.
  //    One transaction per pool, V1 first. Before anything is simulated, every epoch is checked
  //    against the chain (published? already claimed? does OUR proof verify against the on-chain
  //    root with OUR exact amount?). A proof that does not verify means this page's reward list is
  //    stale (the epoch was rebuilt/republished after it loaded): the list is refreshed from the
  //    gateway once and the whole claim is re-planned. Every failure names its reason.
  const runClaims = async (list: UnclaimedEpoch[], allowRefresh: boolean): Promise<void> => {
    const groups = new Map<string, UnclaimedEpoch[]>();
    for (const e of list) {
      const pool = e.poolAddress ? getAddress(e.poolAddress) : v1;
      if (!groups.has(pool)) groups.set(pool, []);
      groups.get(pool)!.push(e);
    }
    const order = [...groups.keys()].sort((a, b) => (a === v1 ? -1 : b === v1 ? 1 : 0));

    for (const pool of order) {
      const group = groups.get(pool)!;
      const isV2 = v2 !== null && pool === v2;
      if (!isV2 && pool !== v1) {
        throw new Error(
          `These rewards were published on pool ${pool}, which this site is not configured for` +
            (pool !== v1 && !v2 ? ' (LossRewardPoolV2 address not set).' : '.')
        );
      }

      // Exact arguments. amountWei is the gateway's exact figure when present; the fallback is the
      // identical rounding the worker used to build the leaf, so the two can't drift.
      const sorted = [...group].sort((a, b) => a.epochNumber - b.epochNumber);
      const planned: { e: UnclaimedEpoch; amount: bigint; proof: `0x${string}`[]; ctx: ClaimEpochContext }[] = [];
      for (const e of sorted) {
        const amount = e.amountWei ? BigInt(e.amountWei) : BigInt(Math.round(e.finalRewardEth * 1e18));
        const proof = (Array.isArray(e.merkleProof) ? e.merkleProof : []) as `0x${string}`[];
        // On-chain state for this epoch: root + claimed flag.
        const [root, claimed] = await Promise.all([
          publicClient.readContract({ address: pool, abi: LOSS_POOL_ABI, functionName: 'epochMerkleRoots', args: [token, BigInt(e.epochNumber)] } as any) as Promise<`0x${string}`>,
          publicClient.readContract({ address: pool, abi: LOSS_POOL_ABI, functionName: 'hasClaimed', args: [token, BigInt(e.epochNumber), wallet] } as any) as Promise<boolean>,
        ]);
        if (claimed) {
          anyAlreadyClaimed = true;
          continue;
        }
        const ZERO = '0x0000000000000000000000000000000000000000000000000000000000000000';
        if (!root || root === ZERO) {
          throw new Error(`Epoch #${e.epochNumber} is not published on ${isV2 ? 'LossRewardPoolV2' : 'LossRewardPool'} ${pool.slice(0, 6)}…${pool.slice(-4)} yet (no Merkle root on-chain). The worker publishes every 5 minutes; try again shortly.`);
        }
        if (!verifyClaimProof(claimLeaf(token, e.epochNumber, wallet, amount), proof, root)) {
          if (allowRefresh) {
            console.warn(`[claim] epoch #${e.epochNumber}: proof/amount on this page does not match the on-chain root ${root}; refreshing the reward list from the gateway and re-planning`);
            const fresh = await fetchLossRewardData(token, wallet);
            return runClaims(fresh.claimable.unclaimedEpochs || [], false);
          }
          throw new Error(
            `Epoch #${e.epochNumber}: the proof and amount served for this wallet (${formatEther(amount)} ETH) do not match the Merkle root published on-chain (${root.slice(0, 10)}…), even after refreshing. ` +
              `The epoch was republished with different allocations; the gateway data is stale or wrong — nothing was sent.`
          );
        }
        planned.push({ e, amount, proof, ctx: { epochNumber: e.epochNumber, amountWei: amount, pool, onChainRoot: root } });
      }
      if (planned.length === 0) continue;

      const epochIds = planned.map((p) => BigInt(p.e.epochNumber));
      const amounts = planned.map((p) => p.amount);
      const proofs = planned.map((p) => p.proof);
      const groupWei = amounts.reduce((sum, a) => sum + a, 0n);

      let call: any;
      let assetSymbol: string | undefined;
      if (isV2) {
        // Stock-paying token: bound the swap with a quote minus the user's slippage setting
        // (the caller is the beneficiary, so this bound is aligned); ETH-paying token: 0.
        const { minAssetOut, asset } = await computeMinAssetOut(token, groupWei, options.slippagePct ?? 1);
        assetSymbol = asset?.symbol;
        const deadline = claimDeadline(chainTs);
        call =
          planned.length === 1
            ? { functionName: 'claimRewardAs', args: [token, epochIds[0], amounts[0], proofs[0], minAssetOut, deadline] }
            : { functionName: 'claimBatchAs', args: [token, epochIds, amounts, proofs, minAssetOut, deadline] };
      } else {
        call =
          planned.length === 1
            ? { functionName: 'claimReward', args: [token, epochIds[0], amounts[0], proofs[0]] }
            : { functionName: 'claimBatch', args: [token, epochIds, amounts, proofs] };
      }

      // Pre-flight the exact call as the sender so a revert surfaces BY NAME (AlreadyClaimed,
      // InvalidProof, MinOutNotMet, DeadlineExpired, …) BEFORE the wallet prompts. viem's
      // shortMessage for a decoded custom error is the bare "reverted." — explainClaimRevert reads
      // the decoded name and arguments off the error chain instead.
      try {
        await publicClient.simulateContract({ address: pool, abi: LOSS_POOL_ABI, account: wallet, ...call } as any);
      } catch (err: any) {
        const explained = explainClaimRevert(err, { epochs: planned.map((p) => p.ctx), pool, isV2, slippagePct: options.slippagePct ?? 1, assetSymbol, chainSkewSec });
        console.error(`[claim] pre-flight ${call.functionName} on ${pool} reverted: ${explained.code}(${explained.args.join(', ')})`, err);
        if (explained.code === 'AlreadyClaimed') {
          anyAlreadyClaimed = true;
          continue;
        }
        if (explained.code === 'InvalidProof' && allowRefresh) {
          const fresh = await fetchLossRewardData(token, wallet);
          return runClaims(fresh.claimable.unclaimedEpochs || [], false);
        }
        throw new Error(`Claim on ${isV2 ? 'the V2 pool' : 'the V1 pool'} would revert on-chain — ${explained.message}`);
      }

      // Send from the user's wallet — identical mechanics to swaps.
      const data = encodeFunctionData({ abi: LOSS_POOL_ABI, ...call } as any);
      const txHash = (await provider.request({
        method: 'eth_sendTransaction',
        params: [{ from: sender, to: pool, data }],
      })) as string;
      await waitForTransactionReceipt(txHash, {
        description: 'Reward claim',
        revertedMessage: 'Reward claim reverted on-chain. Nothing was paid out.',
      });
      txHashes.push(txHash);
      totalWei += groupWei;
    }
  };

  await runClaims(epochs, true);

  if (txHashes.length === 0) {
    return { success: true, txHash: null, txHashes: [], claimedEth: '0', alreadyClaimed: anyAlreadyClaimed, message: 'Rewards were already claimed on-chain.' };
  }
  return { success: true, txHash: txHashes[0], txHashes, claimedEth: formatEther(totalWei) };
};

export type LossPoolBalances = {
  /** LossRewardPoolV2.getUnallocatedBalance(token) — null when V2 is not configured. */
  v2UnallocatedWei: bigint | null;
  /** The V1 pool's unallocated balance (drains per token, then stays 0). */
  v1UnallocatedWei: bigint;
};

/**
 * Per-pool unallocated balances for a token, read on-chain (no DB snapshot: the snapshot's
 * loss_pool_tvl_eth predates V2 and cannot say which pool it describes).
 */
export const getLossPoolBalances = async (tokenAddress: string): Promise<LossPoolBalances> => {
  const tokenAddr = getAddress(tokenAddress);
  const read = async (pool: `0x${string}`) =>
    BigInt((await publicClient.readContract({ address: pool, abi: LOSS_POOL_ABI, functionName: 'getUnallocatedBalance', args: [tokenAddr] } as any)) as bigint);
  const v1 = await read(getAddress(LOSS_REWARD_POOL)).catch(() => 0n);
  const v2 = isV2Configured() ? await read(getAddress(LOSS_REWARD_POOL_V2)).catch(() => null) : null;
  return { v2UnallocatedWei: v2, v1UnallocatedWei: v1 };
};

/**
 * Query the total available ETH in the token's Loss Reward Pool.
 */
export const getLossRewardPoolTVL = async (tokenAddress: string): Promise<number> => {
  try {
    const token = tokenAddress.toLowerCase();
    const { data } = await supabase
      .from('token_market_snapshots_evm')
      .select('loss_pool_tvl_eth')
      .eq('token_address', token)
      .maybeSingle();

    if (data?.loss_pool_tvl_eth && Number(data.loss_pool_tvl_eth) > 0) {
      return Number(data.loss_pool_tvl_eth);
    }

    const tokenAddr = getAddress(tokenAddress);
    const pool = getAddress(LOSS_REWARD_POOL);
    const result = await publicClient.readContract({
      address: pool,
      abi: LOSS_POOL_ABI,
      functionName: 'getUnallocatedBalance',
      args: [tokenAddr],
    } as any);
    return Number(result) / 1e18;
  } catch {
    return 0;
  }
};
